import { byId } from "@utils/dom";
import type { DialogReturnValue } from "@app-types/dom.types";

/**
 * Open a dialog and wait for user response
 * @param dialogId - The ID of the dialog element to open
 * @returns Promise that resolves with the button value clicked
 */
export function openDialog(dialogId: string): Promise<DialogReturnValue> {
  return new Promise((resolve) => {
    const dialog = byId<HTMLDialogElement>(dialogId);

    if (!dialog) {
      console.error(`Dialog with ID "${dialogId}" not found`);
      resolve("cancel");
      return;
    }

    // Store the currently focused element to return focus later
    const previouslyFocused = document.activeElement as HTMLElement | null;

    // Handle dialog close event
    const handleClose = (): void => {
      const returnValue = dialog.returnValue || "cancel";

      // Clean up event listeners
      dialog.removeEventListener("close", handleClose);
      dialog.removeEventListener("click", handleBackdropClick);

      // Return focus to the element that opened the dialog
      if (previouslyFocused?.focus) {
        previouslyFocused.focus();
      }

      resolve(returnValue);
    };

    // Handle backdrop clicks (clicking outside the dialog)
    const handleBackdropClick = (event: MouseEvent): void => {
      const rect = dialog.getBoundingClientRect();
      const isInDialog =
        rect.top <= event.clientY &&
        event.clientY <= rect.top + rect.height &&
        rect.left <= event.clientX &&
        event.clientX <= rect.left + rect.width;

      if (!isInDialog) {
        dialog.close("cancel");
      }
    };

    // Attach event listeners
    dialog.addEventListener("close", handleClose);
    dialog.addEventListener("click", handleBackdropClick);

    // Open the dialog as modal (with backdrop)
    dialog.showModal();
  });
}

/**
 * Close a dialog programmatically
 * @param dialogId - The ID of the dialog element to close
 * @param returnValue - Optional return value (defaults to "cancel")
 */
export function closeDialog(
  dialogId: string,
  returnValue: DialogReturnValue = "cancel"
): void {
  const dialog = byId<HTMLDialogElement>(dialogId);

  if (dialog?.open) {
    dialog.close(returnValue);
  }
}
